// Ultimate Card Game (UCG) - Bot AI difficulty profiles (data only)
//
// One entry per difficulty ('easy' | 'medium' | 'hard' | 'insane', the
// values of #select-ai-difficulty), so difficulty stays a matter of data
// rather than scattered if/else. What each tier contains follows the
// measurements in docs/AI_OPPONENT_IMPLEMENTATION_PLAN_2026-09-22.md
// ("Stand nach Phase 2"): in this income-bound game strength comes from
// WHEN plasma is spent (saving up into waves) and from income itself -
// card-choice quality (scoring reforms, deliberate mistakes, reaction
// delay) measured as no lever at all.
//
// Top level (read by BotAI):
//   decisionIntervalMultiplier  x ~0.714s between decision ticks
//   counterSensitivity          weight of the situational counter bonuses
//   maxBurstActions             action budget cap per tick at high plasma
//   minActionsPerTick           action budget below 45 plasma
//   savingGuard / holdTriplets  the scorer's "wait for the key card" and
//                               "keep a pair for the third copy" biases
//   waves                       null (stream every affordable card), or the
//                               wave planner's config (js/engine/ai/wavePlanner.js)
//   incomeMultiplier            bot's plasma income factor; > 1 only where
//                               the UI declares it (design decision E1:
//                               Insane may "cheat" openly)
//   openingGrace                seconds at match start before bot deploys cards (unless player attacks)
//   pacingInterval              minimum seconds between card deployments when battlefield is calm
//
// nuke (read by js/engine/ai/nukeController.js):
//   earliest          s of match time before the superweapon is considered
//   valueStrikes      false = critical base defense only
//   theta             base firing threshold for the utility U (plasma units)
//   minSwing          board-value lead the enemy must have for a value strike
//   kRefill / kDanger weights of the refill advantage R and base danger D in U
//   arming            null, or { swing, reserve, reserveVsLiveNuke }: bank
//                     `reserve` plasma once the enemy leads by `swing`
//                     (`reserveVsLiveNuke` while the opponent still has one)
//   hold              no own plays between firing and impact
//   flood             one max-budget burst right after impact
//   opponentAware     theta +insuranceTheta while the opponent still holds its
//                     superweapon (the last reset wins), -lastResetTheta after
//   insurance         null, or { lead, reserve }: while the opponent's
//                     superweapon is live and we lead by `lead`, keep
//                     `reserve` plasma back instead of feeding the board
//   lateGameStart     s after which theta decays (use it or lose it); null = never
//
// chassisBias (read by BotAI.selectDoctrine -> js/data/aiDecks.js, 0 if absent):
// how strongly the procedural deck builder prefers the archetypes that measured
// strong (CHASSIS_STRENGTH) over a uniform pick. 0 = uniform (every shipped
// tier), 1 = strongest decks about 80% of the time. Measured +9 points in
// vsplayer at Hard; not enabled by any tier yet because it moves the ladder
// (docs/AI_MASTERPLAN_2026-10.md, "Stand nach B20").
//
// strategist (read by js/engine/ai/strategist.js, null/absent = no plans): the
// hidden match plans of js/engine/ai/gambits.js - { pool, curiosity, planB,
// minCommit, reevalSec, jitter }. Easy and Normal follow a few harmless plans
// and keep one for the whole match (planB false, a lot of curiosity: a beginner
// trying things); Hard and up draw a new plan when one ends. Measured at no
// cost to the ladder (+-1 point on every tier, docs/AI_MASTERPLAN_2026-10.md,
// "Stand nach Phase 2").
//
// humanizer (read by js/engine/ai/humanizer.js, null/absent = off): { latency,
// attention, rhythm, hesitation, mood, errors } - how late the bot's picture of
// the enemy is, how it paces its clicks, where it pauses, how setbacks rattle it
// and (Easy/Normal) when it slips. Only perception is delayed, never plasma,
// hand or output. Measured at no cost to the ladder (+-2 points on every tier,
// docs/AI_MASTERPLAN_2026-10.md, "Stand nach Phase 3").
//
// memoryInfluence (read by strategist.js and ai.js): how much the persistent memory
// of the player (js/engine/ai/opponentMemory.js) counts: 0 = it is collected but not
// used (Easy), up to 1 = fully (Very Hard and up). Scales what past matches taught
// the plan bandit and the habit priors; at 0.5 and above a returning player's deck
// of an earlier session is countered from the first match.
//
// omniscience (read by js/engine/ai/perception.js): which hidden-information
// advantages the tier uses, see OMNISCIENCE_ALL below.

// The information advantages (design decision E5, docs/AI_MASTERPLAN_2026-10.md). true = the bot reads the
// true value, false = it has to make do with what a human could see or infer.
//   enemyPlasma            the opponent's exact plasma (punish timing, refill advantage)
//   enemyHand              the opponent's hand (valuing Mental Extraction)
//   cloakLeak              audio flags and unit flags that betray an undetected Anti Shade
//   freeDeadModuleDiscard  a module card that can never be built is dropped from the hand for free
export const OMNISCIENCE_ALL = Object.freeze({
  enemyPlasma: true,
  enemyHand: true,
  cloakLeak: true,
  freeDeadModuleDiscard: true
});

import { STARTER_POOL } from './gambits.js';

const NUKE_BASE = {
  earliest: 90,
  valueStrikes: true,
  theta: 55,
  minSwing: 25,
  kRefill: 0,
  kDanger: 0.5,
  arming: null,
  hold: false,
  flood: false,
  opponentAware: false,
  insuranceTheta: 20,
  lastResetTheta: 10,
  insurance: null,
  lateGameStart: null,
  lateGameDecayPerMin: 15,
  lateGameMaxDecay: 30,
  finisherEnemyBaseHp: 0.3
};

// Optional nuke behaviours the controller implements and tests, but that
// no tier enables yet: in 2x240 headless A/B matches (npm run ai:eval, seed
// bases 5000/9000) none of them beat the plain value strike + hold -
// banking+flood+awareness 56% vs no-nuke against the plain rules' 60-65%,
// awareness alone at any insuranceTheta 43-53% vs the legacy heuristic.
// Kept for re-measurement as the game's pace changes.
export const NUKE_FEATURE_PRESETS = {
  banking: { arming: { swing: 30, reserve: 40, reserveVsLiveNuke: 40 }, flood: true },
  opponentAware: { opponentAware: true },
  insurance: { insurance: { lead: 60, reserve: 40 } }
};

// Wave planner config (js/engine/ai/wavePlanner.js):
//   threshold      spendable plasma a wave waits for (30 measured best:
//                  20 lost 37% against it, 40 was even)
//   jitter         + rng * jitter on top, re-rolled per wave, so the rhythm
//                  can't be learned by heart
//   releaseSeconds how long a released wave keeps spending
//   defendFactor   defend (play at once) while the enemy's value in our half
//                  exceeds ours there times this factor
const WAVES = { threshold: 30, jitter: 8, releaseSeconds: 1.5, defendFactor: 1.0 };

export const DIFFICULTY_PROFILES = {
  easy: {
    decisionIntervalMultiplier: 2.0,
    counterSensitivity: 0.3,
    maxBurstActions: 1,
    minActionsPerTick: 1,
    savingGuard: false,
    holdTriplets: false,
    proceduralDeck: false,
    waves: null,
    incomeMultiplier: 1.0,
    openingGrace: 10,
    pacingInterval: 3.5,
    omniscience: OMNISCIENCE_ALL,
    strategist: { pool: STARTER_POOL, curiosity: 0.35, planB: false },
    memoryInfluence: 0,
    comboLookahead: 0.0,
    opportunism: 0.0,
    imitateCombos: false,
    humanizer: { latency: { median: 1.2, sigma: 0.5 }, attention: 0.5, rhythm: { cv: 0.7, burstP: 0.2, burstGap: 0.4 }, hesitation: { minCost: 30, range: [0.5, 1.5], nuke: false }, mood: { susceptibility: 1 }, errors: { tau: 15, slipRate: 0.05 } },
    // Superweapon only as a last resort (base nearly lost): a bot that
    // never cashes in a value strike loses ~63% to Normal.
    nuke: { ...NUKE_BASE, valueStrikes: false }
  },
  medium: {
    // The pre-2026-09 bot's play pattern, unchanged for Normal players.
    decisionIntervalMultiplier: 1.0,
    counterSensitivity: 0.75,
    maxBurstActions: 2,
    minActionsPerTick: 1,
    savingGuard: true,
    holdTriplets: true,
    proceduralDeck: false,
    waves: null,
    incomeMultiplier: 1.0,
    openingGrace: 0,
    pacingInterval: 0,
    omniscience: OMNISCIENCE_ALL,
    strategist: { pool: [...STARTER_POOL, 'turtleCounter'], curiosity: 0.25, planB: false },
    memoryInfluence: 0.3,
    comboLookahead: 0.35,
    opportunism: 0.40,
    imitateCombos: false,
    humanizer: { latency: { median: 0.8, sigma: 0.5 }, attention: 0.7, rhythm: { spread: true, followUp: [0.25, 0.6] }, hesitation: true, mood: { susceptibility: 0.5 }, errors: { tau: 6, slipRate: 0.02 } },
    // Value strike + hold: 60-65% vs a bot without a superweapon (legacy
    // heuristic: 56-60%), never feeds its own warhead.
    nuke: { ...NUKE_BASE, kRefill: 0.3, hold: true, lateGameStart: 360 }
  },
  hard: {
    decisionIntervalMultiplier: 0.75,
    counterSensitivity: 1.0,
    maxBurstActions: 3,
    minActionsPerTick: 1,
    savingGuard: true,
    holdTriplets: true,
    proceduralDeck: true,
    // Saving into waves: the one quality lever that measured (65% vs Normal
    // with superweapons off).
    waves: { ...WAVES, punish: true },
    incomeMultiplier: 1.0,
    openingGrace: 0,
    pacingInterval: 0,
    omniscience: OMNISCIENCE_ALL,
    strategist: { curiosity: 0.15 },
    memoryInfluence: 0.7,
    comboLookahead: 0.75,
    opportunism: 0.80,
    imitateCombos: false,
    humanizer: { latency: { median: 0.5, sigma: 0.5 }, attention: 0.85, rhythm: { spread: true, followUp: [0.15, 0.4] }, hesitation: true, variance: { tau: 3.2 } },
    chassisBias: 0.5,
    nuke: { ...NUKE_BASE, earliest: 60, kRefill: 0.3, hold: true, lateGameStart: 300 }
  },
  veryHard: {
    decisionIntervalMultiplier: 0.6,
    counterSensitivity: 1.1,
    maxBurstActions: 4,
    minActionsPerTick: 1,
    savingGuard: true,
    holdTriplets: true,
    proceduralDeck: true,
    waves: { ...WAVES, threshold: 32, jitter: 6, punish: true },
    incomeMultiplier: 1.05,
    openingGrace: 0,
    pacingInterval: 0,
    omniscience: OMNISCIENCE_ALL,
    strategist: { curiosity: 0.12 },
    memoryInfluence: 1.0,
    comboLookahead: 1.0,
    opportunism: 1.0,
    imitateCombos: true,
    humanizer: { latency: { median: 0.4, sigma: 0.5 }, attention: 0.9, rhythm: { spread: true, followUp: [0.15, 0.4] }, hesitation: true, mood: { susceptibility: -0.5 }, variance: { tau: 2.5 } },
    chassisBias: 0.75,
    nuke: { ...NUKE_BASE, earliest: 60, kRefill: 0.35, hold: true, flood: true, lateGameStart: 300 }
  },
  insane: {
    decisionIntervalMultiplier: 0.5,
    counterSensitivity: 1.2,
    maxBurstActions: 4,
    minActionsPerTick: 2,
    savingGuard: true,
    holdTriplets: true,
    proceduralDeck: true,
    waves: { ...WAVES, punish: true },
    // Declared in the difficulty label (index.html) - keep them in sync,
    // tests/ai_difficulty_test.mjs checks it.
    incomeMultiplier: 1.10,
    openingGrace: 0,
    pacingInterval: 0,
    omniscience: OMNISCIENCE_ALL,
    strategist: { curiosity: 0.10 },
    memoryInfluence: 1.0,
    comboLookahead: 1.0,
    opportunism: 1.0,
    imitateCombos: true,
    humanizer: { latency: { median: 0.3, sigma: 0.5 }, attention: 0.95, rhythm: { spread: true, followUp: [0.15, 0.4] }, hesitation: true, mood: { susceptibility: -0.5 }, variance: { tau: 2.0 } },
    chassisBias: 1.0,
    nuke: { ...NUKE_BASE, earliest: 60, kRefill: 0.3, hold: true, lateGameStart: 300 }
  },
  invincible: {
    decisionIntervalMultiplier: 0.4,
    counterSensitivity: 1.35,
    maxBurstActions: 5,
    minActionsPerTick: 2,
    savingGuard: true,
    holdTriplets: true,
    proceduralDeck: true,
    waves: { ...WAVES, threshold: 36, jitter: 4, releaseSeconds: 2.0, defendFactor: 0.95, punish: true },
    incomeMultiplier: 1.20,
    openingGrace: 0,
    pacingInterval: 0,
    omniscience: OMNISCIENCE_ALL,
    strategist: { curiosity: 0.10 },
    memoryInfluence: 1.0,
    comboLookahead: 1.0,
    opportunism: 1.0,
    imitateCombos: true,
    humanizer: { latency: { median: 0.25, sigma: 0.5 }, attention: 1, rhythm: { spread: true, followUp: [0.15, 0.4] }, hesitation: true, mood: { susceptibility: -0.5 }, variance: { tau: 1.5 } },
    chassisBias: 1.0,
    nuke: { ...NUKE_BASE, earliest: 45, kRefill: 0.4, hold: true, flood: true, opponentAware: true, insurance: { lead: 60, reserve: 35 }, lateGameStart: 240 }
  }
};

// Unknown values fall back to the insane tier, as BotAI.setDifficulty()'s
// final else-branch always did.
export function getDifficultyProfile(difficulty) {
  return DIFFICULTY_PROFILES[difficulty] || DIFFICULTY_PROFILES.insane;
}
